import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, FileDown, RefreshCw, Search, Globe, Activity } from "lucide-react";
import ClientPicker from "@/components/ClientPicker";

const REGIONS = ["us-east-1", "us-west-2", "eu-west-1", "eu-central-1", "ap-south-1", "ap-southeast-1"];

function formatErr(d) { if (!d) return "Error"; if (typeof d === "string") return d; if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" "); return String(d); }

export default function CompliancePage() {
    const [clientId, setClientId] = useState("");
    const [snapshot, setSnapshot] = useState(null);
    const [frameworks, setFrameworks] = useState([]);
    const [regionsInventory, setRegionsInventory] = useState(null);
    const [violations, setViolations] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [framework, setFramework] = useState("");

    useEffect(() => {
        api.get("/admin/compliance/frameworks").then((r) => setFrameworks(r.data.frameworks || [])).catch(() => {});
        api.get("/admin/regions").then((r) => setRegionsInventory(r.data)).catch(() => {});
        api.get("/admin/regions/violations").then((r) => setViolations(r.data)).catch(() => {});
    }, []);

    const loadSnapshot = async () => {
        if (!clientId.trim()) {
            toast.error("client_id required");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/compliance/${encodeURIComponent(clientId.trim())}/snapshot`);
            setSnapshot(data);
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = async (fw) => {
        if (!clientId.trim()) {
            toast.error("client_id required");
            return;
        }
        setDownloading(fw || "all");
        try {
            const url = `/admin/compliance/${encodeURIComponent(clientId.trim())}/certificate.pdf${fw ? `?framework=${fw}` : ""}`;
            const res = await api.get(url, { responseType: "blob" });
            const blobUrl = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = `compliance-${clientId.trim()}${fw ? "-" + fw : ""}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
            toast.success("Certificate downloaded");
        } catch (e) {
            toast.error("PDF generation failed");
        } finally {
            setDownloading(null);
        }
    };

    return (
        <div className="px-8 py-8" data-testid="compliance-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED] flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Compliance</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Per-Client Compliance Certificates</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Generate a tamper-evident, framework-mapped certificate for any tenant —
                        derived directly from the audit chain and live inventory. PDFs are signed
                        with a SHA-256 hash of the chain head + content snapshot.
                    </p>
                </div>
            </div>

            {/* Frameworks list */}
            <div className="grid grid-cols-5 gap-2 mb-5" data-testid="compliance-frameworks">
                {frameworks.map((fw) => (
                    <div key={fw.key} className="bg-[#0a0a0a] border border-white/10 rounded-md p-3" data-testid={`compliance-fw-${fw.key}`}>
                        <div className="font-display text-base">{fw.name}</div>
                        <div className="text-[11px] text-white/55 mt-1">{fw.description}</div>
                        <div className="text-[10px] font-mono text-white/40 mt-2">{fw.checks.length} checks</div>
                    </div>
                ))}
            </div>

            {/* Lookup form */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5 mb-5">
                <div className="flex gap-3 items-end">
                    <div className="flex-1">
                        <div className="label-xs mb-1.5">Client</div>
                        <ClientPicker
                            value={clientId}
                            onChange={setClientId}
                            compact
                            width="w-full"
                            placeholder="(select a client…)"
                            testId="compliance-client-id"
                        />
                    </div>
                    <div className="w-48">
                        <div className="label-xs mb-1.5">Framework filter</div>
                        <select value={framework} onChange={(e) => setFramework(e.target.value)} data-testid="compliance-fw-filter"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                            <option value="">All 5 frameworks</option>
                            {frameworks.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
                        </select>
                    </div>
                    <button onClick={loadSnapshot} disabled={loading} data-testid="compliance-load"
                        className="border border-white/15 px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Load snapshot
                    </button>
                    <button onClick={() => downloadPdf(framework)} disabled={downloading} data-testid="compliance-download"
                        className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                        {downloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Download PDF
                    </button>
                </div>
            </div>

            {/* Snapshot preview */}
            {snapshot && (
                <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5 mb-5" data-testid="compliance-snapshot">
                    <div className="flex items-center gap-2 mb-3">
                        <ShieldCheck className="w-4 h-4 text-emerald-300" />
                        <div className="font-display text-base">Snapshot for <code className="text-[#c7a6ff]">{snapshot.client_id}</code></div>
                        <div className="ml-auto text-[11px] font-mono text-white/45">
                            cert: <span className="text-white/75">{snapshot.certificate_hash.slice(0, 24)}…</span>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <div className="label-xs mb-2">Inventory</div>
                            <table className="w-full">
                                <tbody>
                                    {Object.entries(snapshot.inventory).map(([k, v]) => (
                                        <tr key={k} className="border-t border-white/5">
                                            <td className="px-2 py-1 font-mono text-xs">{k}</td>
                                            <td className="px-2 py-1 font-mono text-xs text-white/70">{JSON.stringify(v)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <div className="label-xs mb-2">Audit chain head</div>
                                <div className="text-[11px] font-mono text-white/70">
                                    seq: {snapshot.audit_chain_head?.sequence_no || "—"}
                                    <br />
                                    hash: {(snapshot.audit_chain_head?.current_hash || "—").slice(0, 24)}…
                                </div>
                            </div>
                            <div>
                                <div className="label-xs mb-2">Webhook health</div>
                                <div className="grid grid-cols-3 gap-1">
                                    {Object.entries(snapshot.webhook_health || {}).map(([s, n]) => (
                                        <div key={s} className="text-[11px] font-mono p-1.5 border border-white/10 rounded">
                                            <div className="text-white/55">{s}</div>
                                            <div className="text-white">{n}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="label-xs mb-2">Regions in use</div>
                                <div className="flex flex-wrap gap-1">
                                    {Object.keys(snapshot.regions_in_use || {}).length === 0 ? (
                                        <span className="text-[11px] text-white/40 font-mono">—</span>
                                    ) : Object.entries(snapshot.regions_in_use).map(([r, n]) => (
                                        <span key={r} className="px-1.5 py-0.5 border border-white/10 text-[10px] font-mono rounded">
                                            {r} ({n})
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Region inventory + violations */}
            {regionsInventory && (
                <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5" data-testid="compliance-regions">
                    <div className="flex items-center gap-2 mb-3">
                        <Globe className="w-4 h-4 text-[#7C3AED]" />
                        <div className="font-display text-base">Multi-region inventory</div>
                        <span className="ml-auto text-[11px] font-mono text-white/45">
                            your role <span className="text-white/75">{regionsInventory.viewer_role}</span> · permits {regionsInventory.viewer_permitted_regions.length === 0 ? "all" : regionsInventory.viewer_permitted_regions.join(", ")}
                        </span>
                    </div>
                    <table className="w-full">
                        <thead><tr className="text-left">
                            <th className="label-xs px-3 py-2">Collection</th>
                            {REGIONS.map((r) => <th key={r} className="label-xs px-3 py-2">{r}</th>)}
                            <th className="label-xs px-3 py-2">unspecified</th>
                        </tr></thead>
                        <tbody>
                            {Object.entries(regionsInventory.inventory).map(([coll, perRegion]) => (
                                <tr key={coll} className="border-t border-white/5">
                                    <td className="px-3 py-2 font-mono text-xs">{coll}</td>
                                    {REGIONS.map((r) => <td key={r} className="px-3 py-2 font-mono text-xs text-white/70">{perRegion[r] || 0}</td>)}
                                    <td className="px-3 py-2 font-mono text-xs text-white/45">{perRegion["us-east-1"] && Object.keys(perRegion).length === 1 ? "—" : (perRegion[null] || perRegion["null"] || 0)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {violations && violations.violations.length > 0 && (
                        <div className="mt-4 p-3 border border-red-300/30 bg-red-300/5 rounded">
                            <div className="text-xs font-mono text-red-200 mb-2">{violations.total} regional violation(s) detected</div>
                            <div className="text-[11px] font-mono text-white/55 space-y-0.5 max-h-40 overflow-auto">
                                {violations.violations.slice(0, 30).map((v, i) => (
                                    <div key={i} data-testid={`compliance-violation-${i}`}>
                                        {v.collection} · {v.id} · region={v.data_region} · classification={v.classification}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
